import { useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import { downloadExtension, saveBlob } from "../lib/api";
import { extensionVersion } from "../lib/extension";

/** Downloads for staff: the Casa BAS browser extension and how to install it. */
export function ToolsPage() {
  const auth = useAuth();
  const token = auth.user?.access_token;
  const [installed, setInstalled] = useState<string | null | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void extensionVersion().then(setInstalled);
  }, []);

  async function download() {
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      const { blob, filename } = await downloadExtension(token);
      saveBlob(blob, filename);
    } catch (exc) {
      setError(exc instanceof Error ? exc.message : "The download failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="tools">
      <h2>Tools</h2>

      <article className="tool">
        <h3>Casa BAS extension for Chrome</h3>
        <p>
          Fetches Xero's own Activity Statement, including unfiled amounts, and Xero's Excel
          export, then saves them with the client's workpapers. It uses your own Xero sign-in in
          Chrome. Your Xero login stays in Xero's page; only the figures and the file come to BAS.
        </p>

        {installed === undefined && <p className="muted">Checking whether it is installed…</p>}
        {typeof installed === "string" && (
          <div className="notice success"><p>Installed in this browser (version {installed}).</p></div>
        )}
        {installed === null && (
          <p className="muted">Not installed in this browser, or this is not Chrome.</p>
        )}

        <div className="row">
          <button type="button" className="primary" disabled={busy || !token} onClick={() => void download()}>
            {busy ? "Downloading…" : "Download extension"}
          </button>
        </div>
        {error && <div className="field-error" role="alert">{error}</div>}

        <h4>Install it</h4>
        <ol>
          <li>Unzip the downloaded file (right-click, <strong>Extract All</strong>). Move the folder it makes somewhere it will stay, such as Documents.</li>
          <li>In Chrome, open <code>chrome://extensions</code> and turn on <strong>Developer mode</strong> (top right).</li>
          <li>Click <strong>Load unpacked</strong> and choose that folder: the one with <code>manifest.json</code> directly inside it.</li>
          <li>Reload this page. The status above should say it is installed.</li>
        </ol>

        <h4>Use it</h4>
        <ol>
          <li>Sign in to Xero in the same Chrome window, as you normally do.</li>
          <li>On the <strong>Clients</strong> tab, open <strong>Generate workpaper</strong> for a client and pick the period.</li>
          <li>Click <strong>Fetch from Xero</strong>. Check the figures against Xero, tick the box and click <strong>Save to BAS</strong>.</li>
        </ol>
        <p className="muted small">
          To update it, download it again, replace the folder's contents, and click the reload
          arrow on the extension in <code>chrome://extensions</code>.
        </p>
      </article>
    </section>
  );
}
